'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import SchoolSpotlight from './SchoolSpotlight';
import PartnershipSection from './PartnershipSection';

interface CounterData {
  id: number;
  icon: string;
  value: number;
  targetValue: number;
  label: string;
  format: 'number' | 'percentage';
  hasPlus: boolean;
}

interface School {
  id: number;
  schoolName: string;
  location: string;
  image: string;
  alt: string;
  studentsReached: number;
  poundsGrown: number;
  programYear: string;
  testimonial: string;
  teacherName: string;
}

export default function ImpactInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [counters, setCounters] = useState<CounterData[]>([
    { id: 1, icon: 'ScaleIcon', value: 0, targetValue: 600, label: 'pounds of food distributed', format: 'number', hasPlus: true },
    { id: 2, icon: 'UserGroupIcon', value: 0, targetValue: 60, label: 'families impacted', format: 'number', hasPlus: true },
    { id: 3, icon: 'CodeBracketIcon', value: 0, targetValue: 80, label: 'of students became proficient in basic coding languages', format: 'percentage', hasPlus: false },
    { id: 4, icon: 'DocumentCheckIcon', value: 0, targetValue: 100, label: 'authored at least one functional program', format: 'percentage', hasPlus: false }
  ]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const schools: School[] = [
    {
      id: 1,
      schoolName: 'HD Woodson High School',
      location: 'Washington, DC',
      image: "/assets/images/Screenshot_2026-02-01_at_10.24.52_AM-1769959498425.png",
      alt: 'HD Woodson High School modern building exterior with glass windows and landscaped grounds',
      studentsReached: 60,
      poundsGrown: 0,
      programYear: '2022-2023',
      testimonial: 'HD Woodson was home to our first two pilot programs. We tested the agricultural technology curriculum twice here, with the founder teaching the program directly. No vertical farm was placed, but the curriculum laid the foundation for all future programs. Students showed incredible enthusiasm for learning sustainable agriculture and technology.',
      teacherName: 'Founder-Led Program'
    },
    {
      id: 2,
      schoolName: 'KIPP DC Legacy College Prep',
      location: 'Washington, DC',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_10678a202-1769277588077.png",
      alt: 'KIPP DC Legacy College Prep school building with vibrant learning environment',
      studentsReached: 45,
      poundsGrown: 250,
      programYear: '2023-2024',
      testimonial: 'Our first paid pilot program! We placed a Gardyn vertical farm at KIPP DC and paid a teacher to facilitate the program in an afterschool club, meeting once a week. Students engaged deeply with both the technology and the hands-on growing experience, producing fresh food for their community.',
      teacherName: 'KIPP DC Afterschool Facilitator'
    },
    {
      id: 3,
      schoolName: 'Montessori School of NC',
      location: 'North Carolina',
      image: "/assets/images/Screenshot_2026-02-05_at_4.10.55_PM-1770325968362.png",
      alt: 'Aerial view of Montessori School of NC campus showing multiple buildings and parking lot',
      studentsReached: 35,
      poundsGrown: 180,
      programYear: '2024-2025',
      testimonial: 'We sent the Gardyn vertical farm and complete curriculum to Montessori School of NC, with a teacher facilitating the program on-site. The hands-on nature of hydroponics aligns perfectly with Montessori philosophy. We are awaiting detailed feedback from the teacher.',
      teacherName: 'Montessori Facilitator'
    },
    {
      id: 4,
      schoolName: 'Ron Brown College Preparatory High School',
      location: 'Washington, DC',
      image: "/assets/images/Screenshot_2026-02-05_at_4.12.58_PM-1770326094613.png",
      alt: 'Ron Brown College Preparatory High School building showing front entrance and school signage',
      studentsReached: 0,
      poundsGrown: 0,
      programYear: 'Starting January 28, 2026',
      testimonial: 'We are excited to launch our program at Ron Brown College Prep on January 28th! The teacher will be facilitating the program, and we have already sent a Gardyn vertical farm to the school. This partnership represents our continued growth and commitment to bringing innovative agricultural technology education to DC students.',
      teacherName: 'Ron Brown Prep - Upcoming Program'
    }
  ];

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isHydrated, hasAnimated]);

  const animateCounters = () => {
    const duration = 2500;
    const steps = 80;
    const stepDuration = duration / steps;

    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      const easedProgress = 1 - Math.pow(1 - progress, 3);

      setCounters((prev) =>
        prev.map((counter) => ({
          ...counter,
          value: Math.floor(counter.targetValue * easedProgress)
        }))
      );

      if (currentStep >= steps) {
        clearInterval(interval);
        setCounters((prev) =>
          prev.map((counter) => ({
            ...counter,
            value: counter.targetValue
          }))
        );
      }
    }, stepDuration);
  };

  const formatNumber = (counter: CounterData): string => {
    const { value, format, hasPlus } = counter;

    if (format === 'percentage') {
      return `${value}%`;
    }

    const formatted = value.toLocaleString();
    return hasPlus ? `${formatted}+` : formatted;
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="animate-pulse">
          <div className="h-96 bg-surface" />
          <div className="max-w-7xl mx-auto px-4 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-surface rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Impact Metrics Section */}
      <section ref={sectionRef} className="py-16 bg-[rgba(79,116,74,1)]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our Impact in Numbers
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Measurable outcomes from our programs across schools and communities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {counters.slice(0, 3).map((counter) => (
              <div
                key={counter.id}
                className="text-center transform hover:scale-105 transition-transform duration-300"
              >
                <div className="flex justify-center mb-4">
                  <Icon name={counter.icon as any} size={48} className="text-white" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {formatNumber(counter)}
                </div>
                <div className="text-sm md:text-base text-white/90">
                  {counter.label}
                </div>
              </div>
            ))}
          </div>
          
          {/* Centered 4th counter under the 2nd counter (60 families impacted) */}
          <div className="flex justify-center mt-8">
            <div className="text-center transform hover:scale-105 transition-transform duration-300 max-w-sm">
              <div className="flex justify-center mb-4">
                <Icon name={counters[3].icon as any} size={48} className="text-white" />
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                {formatNumber(counters[3])}
              </div>
              <div className="text-sm md:text-base text-white/90">
                {counters[3].label}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USC GCSP Partnership Section */}
      <PartnershipSection />

      {/* School Spotlights Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
              School Spotlights
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Discover how schools across the country are implementing Farms for Thought and transforming student learning experiences
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {schools.map((school) => (
              <SchoolSpotlight
                key={school.id}
                schoolName={school.schoolName}
                location={school.location}
                image={school.image}
                alt={school.alt}
                studentsReached={school.studentsReached}
                poundsGrown={school.poundsGrown}
                programYear={school.programYear}
                testimonial={school.testimonial}
                teacherName={school.teacherName}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}